import { axiosInterceptor } from "../request-interceptor";

export type AttachmentRecord = {
    id: string;
    table_name: string;
    record_id: string;
    column_name: string | null;
    filename: string;
    content_type: string | null;
    byte_size: number;
    checksum: string | null;
    status: string;
    visibility: string;
    uploaded_by_user_id: string | null;
    created_at: string;
    updated_at: string;
};

const attachmentPath = (tableName: string, recordId: string, columnName?: string | null) => {
    const base = `/api/records/${tableName}/${recordId}`;
    return columnName ? `${base}/fields/${columnName}/attachments` : `${base}/attachments`;
};

const draftAttachmentPath = (tableName: string, draftToken: string, columnName?: string | null) => {
    const base = `/api/records/${tableName}`;
    return columnName
        ? `${base}/fields/${columnName}/draft_attachments/${draftToken}`
        : `${base}/draft_attachments/${draftToken}`;
};

const servicePortalDraftAttachmentPath = (formId: string, draftToken: string, columnName: string) => (
    `/api/service_portal/forms/${formId}/fields/${columnName}/draft_attachments/${draftToken}`
);

const listAttachments = async (tableName: string, recordId: string, columnName?: string | null) => {
    const response = await axiosInterceptor.get<{ data: AttachmentRecord[] }>(
        attachmentPath(tableName, recordId, columnName)
    );
    return response.data.data;
};

const listDraftAttachments = async (tableName: string, draftToken: string, columnName?: string | null) => {
    const response = await axiosInterceptor.get<{ data: AttachmentRecord[] }>(
        draftAttachmentPath(tableName, draftToken, columnName)
    );
    return response.data.data;
};

const uploadAttachment = async (
    tableName: string,
    recordId: string,
    file: File,
    columnName?: string | null
) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axiosInterceptor.post<{ data: AttachmentRecord }>(
        attachmentPath(tableName, recordId, columnName),
        formData
    );
    return response.data.data;
};

const uploadDraftAttachment = async (
    tableName: string,
    draftToken: string,
    file: File,
    columnName?: string | null
) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axiosInterceptor.post<{ data: AttachmentRecord }>(
        draftAttachmentPath(tableName, draftToken, columnName),
        formData
    );
    return response.data.data;
};

const listServicePortalDraftAttachments = async (formId: string, draftToken: string, columnName: string) => {
    const response = await axiosInterceptor.get<{ data: AttachmentRecord[] }>(
        servicePortalDraftAttachmentPath(formId, draftToken, columnName)
    );
    return response.data.data;
};

const uploadServicePortalDraftAttachment = async (
    formId: string,
    draftToken: string,
    file: File,
    columnName: string
) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axiosInterceptor.post<{ data: AttachmentRecord }>(
        servicePortalDraftAttachmentPath(formId, draftToken, columnName),
        formData
    );
    return response.data.data;
};

const deleteAttachment = async (
    tableName: string,
    recordId: string,
    attachmentId: string,
    columnName?: string | null
) => {
    const response = await axiosInterceptor.delete<{ data: AttachmentRecord }>(
        `${attachmentPath(tableName, recordId, columnName)}/${attachmentId}`
    );
    return response.data.data;
};

const deleteDraftAttachment = async (
    tableName: string,
    draftToken: string,
    attachmentId: string,
    columnName?: string | null
) => {
    const response = await axiosInterceptor.delete<{ data: AttachmentRecord }>(
        `${draftAttachmentPath(tableName, draftToken, columnName)}/${attachmentId}`
    );
    return response.data.data;
};

const deleteServicePortalDraftAttachment = async (
    formId: string,
    draftToken: string,
    attachmentId: string,
    columnName: string
) => {
    const response = await axiosInterceptor.delete<{ data: AttachmentRecord }>(
        `${servicePortalDraftAttachmentPath(formId, draftToken, columnName)}/${attachmentId}`
    );
    return response.data.data;
};

const downloadAttachmentUrl = (
    tableName: string,
    recordId: string,
    attachmentId: string,
    columnName?: string | null
) => `${axiosInterceptor.defaults.baseURL || ""}${attachmentPath(tableName, recordId, columnName)}/${attachmentId}/download`;

const viewAttachmentUrl = (
    tableName: string,
    recordId: string,
    attachmentId: string,
    columnName?: string | null
) => `${axiosInterceptor.defaults.baseURL || ""}${attachmentPath(tableName, recordId, columnName)}/${attachmentId}/view`;

const downloadDraftAttachmentUrl = (
    tableName: string,
    draftToken: string,
    attachmentId: string,
    columnName?: string | null
) => `${axiosInterceptor.defaults.baseURL || ""}${draftAttachmentPath(tableName, draftToken, columnName)}/${attachmentId}/download`;

const viewDraftAttachmentUrl = (
    tableName: string,
    draftToken: string,
    attachmentId: string,
    columnName?: string | null
) => `${axiosInterceptor.defaults.baseURL || ""}${draftAttachmentPath(tableName, draftToken, columnName)}/${attachmentId}/view`;

const downloadServicePortalDraftAttachmentUrl = (
    formId: string,
    draftToken: string,
    attachmentId: string,
    columnName: string
) => `${axiosInterceptor.defaults.baseURL || ""}${servicePortalDraftAttachmentPath(formId, draftToken, columnName)}/${attachmentId}/download`;

const viewServicePortalDraftAttachmentUrl = (
    formId: string,
    draftToken: string,
    attachmentId: string,
    columnName: string
) => `${axiosInterceptor.defaults.baseURL || ""}${servicePortalDraftAttachmentPath(formId, draftToken, columnName)}/${attachmentId}/view`;

const attachmentApi = {
    listAttachments,
    listDraftAttachments,
    uploadAttachment,
    uploadDraftAttachment,
    deleteAttachment,
    deleteDraftAttachment,
    listServicePortalDraftAttachments,
    uploadServicePortalDraftAttachment,
    deleteServicePortalDraftAttachment,
    downloadAttachmentUrl,
    downloadDraftAttachmentUrl,
    downloadServicePortalDraftAttachmentUrl,
    viewAttachmentUrl,
    viewDraftAttachmentUrl,
    viewServicePortalDraftAttachmentUrl,
};

export default attachmentApi;
